import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  BadgeOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const DocumentUpload = () => {
  const navigate = useNavigate();
  const { submitDocuments, uploadImage, refreshUser } = useAuth();

  const profilePicRef = useRef(null);
  const docRef = useRef(null);
  const docBackRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    city: '',
    pincode: '',
    fullAddress: '',
  });

  const [profilePicFile, setProfilePicFile] = useState(null);
  const [docFrontFile, setDocFrontFile] = useState(null);
  const [docBackFile, setDocBackFile] = useState(null);
  const [profilePicPreview, setProfilePicPreview] = useState('');
  const [docFrontPreview, setDocFrontPreview] = useState('');
  const [docBackPreview, setDocBackPreview] = useState('');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleProfilePicSelect = (file) => {
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload only JPG or PNG images');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }
    setProfilePicFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setProfilePicPreview(e.target.result);
    reader.readAsDataURL(file);
    setError('');
  };

  const handleDocFrontSelect = (file) => {
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload only JPG or PNG images');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }
    setDocFrontFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setDocFrontPreview(e.target.result);
    reader.readAsDataURL(file);
    setError('');
  };

  const handleDocBackSelect = (file) => {
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload only JPG or PNG images');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File size should be less than 5MB');
      return;
    }
    setDocBackFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setDocBackPreview(e.target.result);
    reader.readAsDataURL(file);
    setError('');
  };

  /**
   * Compresses an image and returns a raw base64 string (no data: prefix).
   * Targets ≤ 500 KB of actual image bytes to safely fit in a 1 MB JSON body
   * (base64 adds ~33% overhead, plus JSON envelope + headers).
   */
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onerror = reject;
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onerror = reject;
        img.onload = () => {
          // 500 KB raw bytes → base64 string ≤ ~683 KB chars (safe within 1 MB limit)
          const MAX_RAW_BYTES = 500 * 1024;

          const encode = (maxDim, quality) => {
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
              if (width >= height) {
                height = Math.round((height * maxDim) / width);
                width = maxDim;
              } else {
                width = Math.round((width * maxDim) / height);
                height = maxDim;
              }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            // Strip "data:image/jpeg;base64," prefix — send raw base64 only
            const dataUrl = canvas.toDataURL('image/jpeg', quality);
            return dataUrl.split(',')[1];
          };

          // Each pass shrinks both dimensions and quality — max 5 passes
          const passes = [
            [1000, 0.7],
            [800,  0.6],
            [600,  0.5],
            [400,  0.4],
            [300,  0.3],
          ];

          for (const [dim, q] of passes) {
            const b64 = encode(dim, q);
            // base64 char count * 0.75 = actual byte count
            if (b64.length * 0.75 <= MAX_RAW_BYTES) {
              return resolve(b64);
            }
          }

          // Absolute fallback — guaranteed tiny
          resolve(encode(300, 0.3));
        };
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.name || formData.name.length < 2) {
      setError('Name is required (minimum 2 characters)');
      return;
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Valid email is required');
      return;
    }
    if (!formData.city) {
      setError('City is required');
      return;
    }
    if (!profilePicFile) {
      setError('Please upload a profile photo');
      return;
    }
    if (!docFrontFile) {
      setError('Please upload the front side of your ID document');
      return;
    }
    if (!docBackFile) {
      setError('Please upload the back side of your ID document');
      return;
    }
    if (!termsAccepted) {
      setError('Please accept the terms and conditions');
      return;
    }
    

    setLoading(true);

    try {
      setUploadProgress('Compressing & uploading profile photo...');
      const profileBase64 = await compressImage(profilePicFile);
      const profileResp = await uploadImage(
        profileBase64,
        `profile_${Date.now()}`,
        'jpeg'
      );
      const profileUrl = profileResp?.fileUrl || '';

      setUploadProgress('Compressing & uploading document front...');
      const docFrontBase64 = await compressImage(docFrontFile);
      const docFrontResp = await uploadImage(
        docFrontBase64,
        `doc_front_${Date.now()}`,
        'jpeg'
      );
      const docFrontUrl = docFrontResp?.fileUrl || '';

      setUploadProgress('Compressing & uploading document back...');
      const docBackBase64 = await compressImage(docBackFile);
      const docBackResp = await uploadImage(
        docBackBase64,
        `doc_back_${Date.now()}`,
        'jpeg'
      );
      const docBackUrl = docBackResp?.fileUrl || '';

      setUploadProgress('Submitting...');
      await submitDocuments({
        name: formData.name,
        email: formData.email,
        city: formData.city,
        pincode: formData.pincode,
        fullAddress: formData.fullAddress,
        userProfilePic: profileUrl,
        panCardUrl: docFrontUrl,
        panCardBackUrl: docBackUrl,
      });

      await refreshUser();

      setSuccess('Documents submitted successfully! Your registration is pending admin approval.');
      setUploadProgress('');

      setTimeout(() => {
        navigate('/verification-pending');
      }, 2000);
    } catch (error) {
      console.error('Document submission error:', error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.error?.message ||
          'Failed to submit documents. Please try again.'
      );
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  const renderUploadCard = (label, previewUrl, onSelect, onRemove) => (
    <Card variant="outlined" sx={{ mb: 2, height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
        {previewUrl ? (
          <Box>
            <CardMedia
              component="img"
              height="160"
              image={previewUrl}
              alt={label}
              sx={{ objectFit: 'contain', bgcolor: 'grey.100', borderRadius: 1 }}
            />
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton size="small" color="error" onClick={onRemove}>
                <Delete />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'grey.50' },
            }}
            onClick={() => onSelect()}
          >
            <CloudUpload sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Click to upload
            </Typography>
            <Typography variant="caption" color="text.secondary">
              JPG or PNG • Auto-compressed to ≤500 KB
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #006994 0%, #00acc1 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <BadgeOutlined sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Complete Your Profile
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Provide your details and upload documents for verification
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {uploadProgress && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {uploadProgress}
              </Typography>
              <LinearProgress />
            </Box>
          )}

          <form onSubmit={handleSubmit}>
            {/* Personal Information */}
            <Typography variant="h6" gutterBottom>
              Personal Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  disabled={loading}
                  inputProps={{ maxLength: 6 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Full Address"
                  name="fullAddress"
                  value={formData.fullAddress}
                  onChange={handleChange}
                  multiline
                  rows={1}
                  disabled={loading}
                />
              </Grid>
            </Grid>

            {/* Uploads */}
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              Upload Documents
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                {renderUploadCard(
                  'Profile Photo',
                  profilePicPreview,
                  () => profilePicRef.current.click(),
                  () => { setProfilePicFile(null); setProfilePicPreview(''); }
                )}
                <input
                  ref={profilePicRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleProfilePicSelect(e.target.files[0])}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderUploadCard(
                  'ID Document Front (Aadhaar)',
                  docFrontPreview,
                  () => docRef.current.click(),
                  () => { setDocFrontFile(null); setDocFrontPreview(''); }
                )}
                <input
                  ref={docRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleDocFrontSelect(e.target.files[0])}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {renderUploadCard(
                  'ID Document Back (Aadhaar )',
                  docBackPreview,
                  () => docBackRef.current.click(),
                  () => { setDocBackFile(null); setDocBackPreview(''); }
                )}
                <input
                  ref={docBackRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleDocBackSelect(e.target.files[0])}
                />
              </Grid>
            </Grid>

            {/* Terms */}
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={
                  <Typography variant="body2">
                    I confirm that all the information provided is correct and I accept the{' '}
                    <Link href="#" underline="hover">
                      terms and conditions
                    </Link>
                  </Typography>
                }
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading}
            >



              

              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  {uploadProgress || 'Submitting...'}
                </Box>
              ) : (
                'Submit for Verification'
              )}
            </Button>
          </form>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="white">
            © 2026 Friends Services. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default DocumentUpload;